/** @format */
import isPropValid from '@emotion/is-prop-valid';
import { Button, ButtonSize } from '@mpb-com/feather/components/atoms/button';
import { Heading } from '@mpb-com/feather/components/atoms/heading';
import {
    HeadingColor,
    HeadingSize,
    HighlightColor,
} from '@mpb-com/feather/components/atoms/heading/types';
import {
    Skeleton,
    SkeletonColour,
    SkeletonVariant,
} from '@mpb-com/feather/components/atoms/skeleton';
import { MarketLanguage } from '@mpb-com/feather/enums';
import { MediaQuery } from '@mpb-com/feather/helpers/media-queries';
import React, { useContext } from 'react';
import styled from 'styled-components';

import { Image } from '@toucan/common/contentful/types';
import { Breadcrumb as BreadcrumbType } from '@toucan/common/services/taxonomy/config/types';

import { useMediaQuery } from '@toucan/client/global/application';
import { MarketContext } from '@toucan/client/global/market';
import { WrappedLink } from '@toucan/client/global/nextjs';
import { TRUSTPILOT_TEMPLATE_IDS } from '@toucan/client/global/trustpilot';

import {
    ConstrainedWrapper,
    ConstrainedWrapperGutter,
} from '@toucan/client/shared/components/constrained-wrapper';
import { LinkVariant } from '@toucan/client/shared/components/link';
import {
    Trustpilot,
    TrustpilotDisplayType,
    TrustpilotTheme,
} from '@toucan/client/shared/components/trustpilot';
import { renderMarkdown } from '@toucan/client/shared/helpers/markdown';

import {
    Breadcrumb,
    BreadcrumbVariant,
} from '@toucan/client/blocks/components/breadcrumb';

import { ContentfulPicture } from '@toucan/client/features/contentful';

import { styles } from './original-content-header.styles';
import { getButtonVariant } from './style-helpers';

export enum OriginalContentHeaderVariant {
    MAIN = 'main',
    CATEGORY = 'category',
    PROMOTIONS = 'promotions',
    SUSTAINABILITY = 'sustainable',
}

export type OriginalContentHeaderProps = {
    breadcrumb?: BreadcrumbType[];
    title?: string;
    description?: string | null;
    callToActionText?: string | null;
    callToActionHref?: string | null;
    variant: OriginalContentHeaderVariant;
    image?: Image | null;
    'data-testid'?: string;
    isLoading?: boolean;
    className?: string;
    /**
     * @default false
     */
    trustpilotEnabled?: boolean | null;
};

export type StyledOriginalContentHeaderProps = Pick<
    OriginalContentHeaderProps,
    'variant'
> & {
    hasBreadcrumb: boolean;
    trustpilotEnabled: boolean;
    language: MarketLanguage;
};

const StyledOriginalContentHeader = styled.section.withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})<StyledOriginalContentHeaderProps>`
    ${styles}
`;

export const OriginalContentHeader = ({
    title,
    description,
    callToActionText,
    callToActionHref,
    breadcrumb,
    variant,
    image,
    isLoading,
    trustpilotEnabled = false,
    ...otherProps
}: OriginalContentHeaderProps) => {
    const { activeMarket } = useContext(MarketContext);
    const isSmallTablet = useMediaQuery(MediaQuery.SMALL_TABLET);

    const renderSkeleton = () => (
        <div className="content">
            <div className="breadcrumbs">
                <Skeleton
                    variant={SkeletonVariant.TEXT}
                    lineCount={1}
                    colour={SkeletonColour.LIGHT}
                    className="breadcrumb-skeleton"
                />
                <Skeleton
                    variant={SkeletonVariant.CIRCLE}
                    colour={SkeletonColour.LIGHT}
                    className="breadcrumb-skeleton-touch"
                />
            </div>

            <div className="summary">
                <Heading
                    size={HeadingSize.H1}
                    color={HeadingColor.TEXT_INVERSE}
                    highlightColor={HighlightColor.TEXT_ACCENT_INVERSE}
                >
                    <Skeleton variant={SkeletonVariant.TEXT} lineCount={1} />
                </Heading>

                <p>
                    <Skeleton variant={SkeletonVariant.TEXT} lineCount={3} />
                </p>

                <Skeleton
                    variant={SkeletonVariant.TEXT}
                    lineCount={1}
                    className="cta-skeleton"
                />

                <Skeleton
                    variant={SkeletonVariant.TEXT}
                    lineCount={1}
                    className="trustpilot-skeleton"
                />
            </div>

            <div className="image-skeleton">
                <Skeleton variant={SkeletonVariant.BLOCK} />
            </div>
        </div>
    );

    const hasCallToAction = callToActionText && callToActionHref;

    const renderContent = () => (
        <div className="content">
            {breadcrumb && (
                <div className="breadcrumbs">
                    <Breadcrumb
                        links={breadcrumb}
                        mobileVariant={BreadcrumbVariant.DARK}
                        desktopVariant={BreadcrumbVariant.DARK}
                    />
                </div>
            )}

            <div className="summary">
                <Heading
                    size={HeadingSize.H1}
                    color={HeadingColor.TEXT_INVERSE}
                    highlightColor={HighlightColor.TEXT_ACCENT_INVERSE}
                >
                    {renderMarkdown(title)}
                </Heading>

                <p>{description}</p>

                {hasCallToAction && (
                    <WrappedLink
                        variant={LinkVariant.CUSTOM}
                        href={callToActionHref}
                        passHref={true}
                        className="cta"
                    >
                        <Button
                            size={ButtonSize.LARGE}
                            variant={getButtonVariant(variant)}
                        >
                            {callToActionText}
                        </Button>
                    </WrappedLink>
                )}
                {trustpilotEnabled && (
                    <div
                        className="trustpilot-container"
                        data-testid="original-content-header__trustpilot-container"
                    >
                        <Trustpilot
                            templateId={TRUSTPILOT_TEMPLATE_IDS.microCombo}
                            theme={TrustpilotTheme.DARK}
                            width={isSmallTablet ? '100%' : ''}
                            height={!isSmallTablet ? '20px' : '40px'}
                            isLeftAligned={!isSmallTablet}
                            displayType={TrustpilotDisplayType.LONG}
                        />
                    </div>
                )}
            </div>

            <div className="image">
                <ContentfulPicture image={image} widths={[400, 800]} />
            </div>
        </div>
    );

    return (
        <StyledOriginalContentHeader
            variant={variant}
            hasBreadcrumb={Boolean(breadcrumb)}
            language={activeMarket.language}
            trustpilotEnabled={Boolean(trustpilotEnabled)}
            {...otherProps}
        >
            <ConstrainedWrapper gutter={ConstrainedWrapperGutter.SMALL_GUTTER}>
                {isLoading ? renderSkeleton() : renderContent()}
            </ConstrainedWrapper>
        </StyledOriginalContentHeader>
    );
};
